import { DownloadIcon, RefreshIcon } from "./icons"
import { formatBytes } from "../../shared/services"
import { ConversionError, RateLimitError } from "../api/WindowsCursorsApi.errors"

import type { WindowsCursorFile } from "../api/WindowsCursorsApi.types"

const BUTTON_CLASS =
  "btn btn-lg col-span-full gap-2 rounded-2xl text-base font-extrabold md:col-span-1 md:min-w-44"

const errorMessage = (error: Error) => {
  if (error instanceof RateLimitError) return "Too many conversions — try again in a minute."
  if (error instanceof ConversionError && error.message) {
    return `Couldn't convert — try again. ${error.message}.`
  }

  return "Couldn't convert — try again."
}

interface Props {
  outputName: string
  frameCount: number
  isPending: boolean
  // Only passed for an animation.
  isAddingFrames?: boolean
  result: WindowsCursorFile | null
  error: Error | null
  onDownload: () => void
  onDownloadAgain: (file: WindowsCursorFile) => void
}

export const DownloadCard = ({
  outputName,
  frameCount,
  isPending,
  isAddingFrames = false,
  result,
  error,
  onDownload,
  onDownloadAgain,
}: Props) => {
  const isAnimated = frameCount > 1
  const extension = isAnimated ? ".ani" : ".cur"
  const meta = [
    isAnimated ? `${frameCount} frames` : null,
    result ? formatBytes(result.blob.size) : null,
  ]
    .filter(Boolean)
    .join(" · ")
  const tone = result
    ? "bg-[color-mix(in_oklab,var(--color-success)_10%,var(--color-base-300))]"
    : error
      ? "bg-[color-mix(in_oklab,var(--color-error)_10%,var(--color-base-300))]"
      : "bg-[color-mix(in_oklab,var(--color-primary)_8%,var(--color-base-300))]"

  return (
    <div
      className={`grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3 rounded-box px-4 py-3 md:grid-cols-[auto_minmax(0,1fr)_auto] ${tone}`}
    >
      <span
        aria-hidden="true"
        className="grid size-13 place-items-center rounded-lg bg-primary/15 font-display text-sm font-bold text-primary"
      >
        {extension}
      </span>

      <div className="flex min-w-0 flex-col gap-0.5">
        <b className="text-sm leading-tight break-all">{result?.filename ?? outputName}</b>
        {meta && <span className="text-sm font-bold text-muted-content">{meta}</span>}
        <p
          role="status"
          className={`text-xs font-semibold ${result ? "text-success" : error ? "text-error" : "text-muted-content"}`}
        >
          {isPending &&
            (isAnimated
              ? `Encoding ${frameCount} frames × 5 sizes…`
              : "Packing 5 sizes into one file…")}
          {result && "Sent to your downloads."}
          {error && errorMessage(error)}
        </p>
        {result && (
          <a
            href="#install"
            data-controller="scroll-to"
            data-action="scroll-to#scroll"
            className="mt-1 w-fit link text-xs font-bold link-primary link-hover"
          >
            Install guide ↓
          </a>
        )}
      </div>

      {/* aria-disabled, not disabled: a disabled button drops keyboard focus to <body>. */}
      {isPending ? (
        <button type="button" aria-disabled="true" className={`${BUTTON_CLASS} btn-disabled`}>
          <span className="loading loading-sm loading-spinner" aria-hidden="true" />
          Converting…
        </button>
      ) : result ? (
        <button
          type="button"
          onClick={() => onDownloadAgain(result)}
          className={`${BUTTON_CLASS} btn-neutral`}
        >
          <DownloadIcon className="size-5" />
          Download again
        </button>
      ) : (
        // Locked while frames are being added, so the file is never built without them.
        <button
          type="button"
          aria-disabled={isAddingFrames}
          onClick={isAddingFrames ? undefined : onDownload}
          className={`${BUTTON_CLASS} ${isAddingFrames ? "btn-disabled" : "btn-primary"}`}
        >
          {error ? <RefreshIcon className="size-5" /> : <DownloadIcon className="size-5" />}
          {error ? "Try again" : `Download ${extension}`}
        </button>
      )}
    </div>
  )
}
